import { Fragment, useEffect, useState } from 'react';
import { reportApi } from '../utils/api';
import { ChevronLeft, ChevronRight, ChevronDown, Loader, ListChecks, Palmtree, Laptop } from 'lucide-react';
import { format, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

const fmtDays = (n) => {
  const v = Math.round(n * 10) / 10;
  return v % 1 === 0 ? `${v}` : v.toFixed(1);
};

const STATUS_LABEL = {
  approved: 'Aprobada',
  pending: 'Pendiente',
  cancellation_requested: 'Cancelación solicitada',
};

const PERIOD_LABEL = { morning: 'Mañana', afternoon: 'Tarde', full_day: 'Día completo' };

export const ManagerMonthlyDetail = () => {
  const [refDate, setRefDate] = useState(new Date());
  const [data, setData] = useState({ team: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    reportApi.teamMonthlyDetail(refDate.getFullYear(), refDate.getMonth() + 1)
      .then((res) => { if (active) setData(res.data); })
      .catch((e) => { console.error('team-monthly-detail error:', e); if (active) setData({ team: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refDate]);

  const toggle = (userId) => setExpanded((prev) => (prev === userId ? null : userId));

  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-monthly-detail">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-brand" />
          Detalle mensual por empleado
        </h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setRefDate((d) => subMonths(d, 1))}
            data-testid="monthly-detail-prev"
            className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs font-medium capitalize min-w-24 text-center" data-testid="monthly-detail-month-label">
            {format(refDate, 'MMMM yyyy', { locale: es })}
          </span>
          <button
            onClick={() => setRefDate((d) => addMonths(d, 1))}
            data-testid="monthly-detail-next"
            className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-5 w-5 animate-spin text-brand" />
        </div>
      ) : data.team.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-6">Sin miembros en el equipo.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="monthly-detail-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-2 font-medium">Empleado</th>
                <th className="py-2 pr-2 font-medium">Vacaciones</th>
                <th className="py-2 pr-2 font-medium">Teletrabajo</th>
                <th className="py-2 pr-2 font-medium w-6" />
              </tr>
            </thead>
            <tbody>
              {data.team.map((member) => {
                const totalReqs = member.requests.length;
                const isOpen = expanded === member.user_id;
                return (
                  <Fragment key={member.user_id}>
                    <tr
                      onClick={() => totalReqs > 0 && toggle(member.user_id)}
                      data-testid={`monthly-detail-row-${member.user_id}`}
                      className={`border-b border-border/60 last:border-0 ${totalReqs > 0 ? 'cursor-pointer hover:bg-muted/40' : ''} transition-colors`}
                    >
                      <td className="py-2 pr-2 font-medium truncate max-w-[160px]">{member.user_name}</td>
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                          <Palmtree className="h-3 w-3" />
                          {fmtDays(member.vacation_days_approved)}d
                        </span>
                        {member.vacation_days_pending > 0 && (
                          <span className="ml-1.5 text-amber-600 dark:text-amber-400">
                            (+{fmtDays(member.vacation_days_pending)} pdte.)
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400">
                          <Laptop className="h-3 w-3" />
                          {fmtDays(member.remote_days_approved)}d
                        </span>
                        {member.remote_days_pending > 0 && (
                          <span className="ml-1.5 text-amber-600 dark:text-amber-400">
                            (+{fmtDays(member.remote_days_pending)} pdte.)
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        {totalReqs > 0 && (
                          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={4} className="pb-3">
                          <div className="bg-muted/30 rounded-lg p-2.5 space-y-1.5" data-testid={`monthly-detail-expand-${member.user_id}`}>
                            {member.requests.map((req) => (
                              <div key={req.id} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  {req.type === 'vacation' ? (
                                    <Palmtree className="h-3 w-3 text-green-600 dark:text-green-400 flex-shrink-0" />
                                  ) : (
                                    <Laptop className="h-3 w-3 text-sky-600 dark:text-sky-400 flex-shrink-0" />
                                  )}
                                  <span>
                                    {req.start_date === req.end_date
                                      ? req.start_date
                                      : `${req.start_date} → ${req.end_date}`}
                                    {req.period_type && ` · ${PERIOD_LABEL[req.period_type] || req.period_type}`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-muted-foreground">{fmtDays(req.days)}d</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                    req.status === 'approved' ? 'status-approved' :
                                    req.status === 'pending' ? 'status-pending' : 'status-cancelled'
                                  }`}>
                                    {STATUS_LABEL[req.status] || req.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
